import React from "react";
import { Navigate, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useAppConfig, useConfigStatus } from "@/context/ConfigContext";
import { INDICATORS_VIEW_PARAM, isModePending, isOperatorMode } from "@/lib/uiMode";
import { SHELL } from "@/constants/testIds";

/**
 * P0.6 — guarda de `/dashboard`, simétrica à de `/hoje`.
 *
 * Em modo operador o Dashboard não faz parte da navegação: um link antigo,
 * um bookmark ou o fallback de `ProtectedRoute` (que manda para `/dashboard`
 * quem não tem o perfil de uma rota) acabariam num ecrã que o operador já
 * não tem no menu. Aqui volta para `/hoje`.
 *
 * As duas guardas não se podem entrar em ciclo: esta só redirecciona quando
 * `isOperatorMode` é verdadeiro, `OperatorHomeRoute` só quando é falso.
 *
 * Excepção (auditoria 2026-10): "Os meus indicadores", a ligação secundária
 * de "O meu dia", abre `/dashboard?vista=indicadores`. Só essa entrada
 * explícita passa — o fallback e os links antigos continuam a ir para `/hoje`.
 * O âmbito dos dados é decidido pelo servidor, não por esta guarda.
 *
 * Enquanto a config não chegou não se decide nada — ver `isModePending`.
 */
export default function DashboardRoute({ children }) {
  const { user } = useAuth();
  const config = useAppConfig();
  const configStatus = useConfigStatus();
  const { search } = useLocation();

  if (isModePending(user, configStatus)) {
    return (
      <div
        data-testid={SHELL.routeLoading}
        className="min-h-screen flex items-center justify-center bg-surface-bg text-ink-secondary text-sm"
      >
        A carregar…
      </div>
    );
  }

  const wantsIndicators = new URLSearchParams(search).get("vista") === INDICATORS_VIEW_PARAM;
  if (isOperatorMode(user, config) && !wantsIndicators) return <Navigate to="/hoje" replace />;

  return children;
}
